import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Logo } from "@/components/brand/Logo";

const SiteCtx = createContext(null);

function useAnalytics(gaId) {
  useEffect(() => {
    if (!gaId || document.getElementById("ga-script")) return;
    const s = document.createElement("script");
    s.id = "ga-script";
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
    };
    window.gtag("js", new Date());
    window.gtag("config", gaId);
  }, [gaId]);
}

function BootScreen({ error, onRetry }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center" data-testid="boot-screen">
      <Logo className="h-12 w-auto" />
      {error ? (
        <div>
          <p className="text-base text-slate-700">We couldn't load the website. Please check your connection.</p>
          <button onClick={onRetry} className="mt-4 font-semibold text-brand-blue underline" data-testid="boot-retry-btn">
            Try again
          </button>
        </div>
      ) : (
        <div className="h-1 w-32 overflow-hidden rounded-full bg-brand-tint2">
          <div className="h-full w-1/2 animate-pulse rounded-full bg-brand-blue" />
        </div>
      )}
    </div>
  );
}

export function SiteProvider({ children }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    setError(false);
    return api.get("/public/bootstrap").then((r) => setData(r.data)).catch(() => setError(true));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  useAnalytics(data?.settings?.ga_id);
  if (!data) return <BootScreen error={error} onRetry={load} />;
  return <SiteCtx.Provider value={{ settings: data.settings, content: data.content, refresh: load }}>{children}</SiteCtx.Provider>;
}

export const useSite = () => useContext(SiteCtx);
